import { FormValidationFunction } from '@/views/interfaces/vuetify/form.interface';
import { DNSRecordType, SRVRecordProtocols } from '@fenix/dns-entities/dist/dns-record.entity';
import { CAAFlag } from "@/services/DNS/Interfaces";

export default class CreateDNSRecordForm {
  public type: DNSRecordType = DNSRecordType.A;
  public host: string = '';
  public target: string = '';
  public priority: number;
  public service: string = '';
  public protocol: SRVRecordProtocols;
  public weight: number;
  public port: number;
  public ttl: number;

  public caaTag: string = '';
  public caaFlags: number = 0;

  get isCAACritical(): boolean {
    return (this.caaFlags & CAAFlag.IssuerCritical) !== 0;
  }

  set isCAACritical(value: boolean) {
    if (value) {
      this.caaFlags |= CAAFlag.IssuerCritical;
    } else {
      this.caaFlags &= ~CAAFlag.IssuerCritical;
    }
  }

  public listTypes: string[] = [
    'A',
    'AAAA',
    'CNAME',
    'NS',
    'MX',
    'TXT',
    'PTR',
    'SRV',
    'CAA',
  ];

  public listSRVProtocols: string[] = [
    'TCP',
    'UDP',
  ];

  public caaRecordTags: string[] = [
    'issue',
    'issuewild',
    'iodef',
  ];

  private readonly dnsLabelRegex = /^[A-Za-z0-9_](?:[A-Za-z0-9_-]{0,61}[A-Za-z0-9_])?$/;

  private isValidDnsName(
    value: string,
    allowApex = false,
    allowRoot = false,
  ): boolean {
    const name = value.trim();

    if (allowApex && (!name || name === '@')) {
      return true;
    }

    if (allowRoot && name === '.') {
      return true;
    }

    if (!name || name.length > 253) {
      return false;
    }

    return name
      .split('.')
      .every(label => this.dnsLabelRegex.test(label));
  }

  public rules: { [name: string]: FormValidationFunction[] } = {
    typeRules: [
      (v: string) => !!v || 'List has to have a type',
      (v: string) => this.listTypes.indexOf(v) !== -1 || 'Invalid list type',
    ],
    hostRules: [
      (v: string) => {
        const allowApex = this.type !== 'CNAME';

        return this.isValidDnsName(v, allowApex)
          || `Please enter a valid ${this.type} record name.`;
      },
    ],
    targetRules: [
      (v: string) => !!v || 'Target has to have a value',
      (v: string) => {
        const errors = [];

        if (this.type === 'A' && !v.match(/^((25[0-5]|2[0-4][0-9]|1{0,1}[0-9]{1,2})\.){3}(25[0-5]|2[0-4][0-9]|1{0,1}[0-9]{1,2})$/g)) {
          errors.push('Please enter a valid IPv4 address');
        }

        if (this.type === 'AAAA' && !v.match(/^((([0-9A-Fa-f]{1,4}:){1,6}:)|(([0-9A-Fa-f]{1,4}:){7}))([0-9A-Fa-f]{1,4})$/g)) {
          errors.push('Please enter a valid IPv6 address');
        }

        if (
          ['CNAME', 'MX', 'NS', 'PTR'].includes(this.type)
          && !this.isValidDnsName(v)
        ) {
          errors.push( 'Target must be a valid DNS name.');
        }

        if (
          this.type === 'SRV'
          && !this.isValidDnsName(v, false, true)
        ) {
          errors.push('Target must be a valid DNS name.');
        }

        if (errors.length > 0) {
          return errors.join(' ');
        }

        return true;
      },
    ],
    priorityRules: [
      (v: number) => {
        if (!v && (this.type === 'MX' || this.type === 'SRV')) {
          return 'Priority field is required';
        }

        return true;
      },
    ],
    protocolRules: [
      (v: string) => !!v || 'Protocol has to have a type',
      (v: string) => this.listSRVProtocols.indexOf(v) !== -1 || 'Invalid protocol type',
    ],
    weightRules: [
      (v: number) => v >= 0 || 'Weight should be positive number',
    ],
    portRules: [
      (v: number) => (v >= 0 && v <= 65535) || 'Port should be positive number between 0 and 65535',
    ],
    ttlRules: [
      (v: number) => (!v || (v >= 300 && v < 864000)) || 'TTL should be positive number between 300 and 864000',
    ],
    caaTagRules: [
      (v: string) => !!v || 'CAA tag is required',
      (v: string) => this.caaRecordTags.indexOf(v) !== -1 || 'Invalid list type',
    ],
    caaFlagRules: [
      (v: number) => ((v >= 0 && v <= 255)) || 'CAA flag should be a number between 0 and 255',
    ],
  };
}
